import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { Mail, Phone, MapPin, Globe, StickyNote, Plus, Edit, Trash2 } from 'lucide-react'
import ContactDetailsForm from './ContactDetailsForm'

interface ContactDetail {
  id: string
  type: string
  label: string
  value: string
  show_in_contact_page: boolean
  show_in_footer: boolean
  is_active: boolean
  display_order: number
  created_at: string
  updated_at: string
}

const ContactDetailsManager = () => {
  const { toast } = useToast()
  const [contactDetails, setContactDetails] = useState<ContactDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [editingContact, setEditingContact] = useState<ContactDetail | null>(null)
  const [showForm, setShowForm] = useState(false)

  const fetchContactDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_details')
        .select('*')
        .order('display_order', { ascending: true })

      if (error) throw error
      setContactDetails(data || [])
    } catch (error) {
      console.error('Error fetching contact details:', error)
      toast({
        title: "Error",
        description: "Failed to fetch contact details.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContactDetails()
  }, [])

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this contact detail?')) return

    try {
      const { error } = await supabase
        .from('contact_details')
        .delete()
        .eq('id', id)

      if (error) throw error

      toast({
        title: "Success",
        description: "Contact detail deleted successfully!",
      })
      
      fetchContactDetails()
    } catch (error) {
      console.error('Error deleting contact detail:', error)
      toast({
        title: "Error",
        description: "Failed to delete contact detail.",
        variant: "destructive",
      })
    }
  }

  const handleFormSave = () => {
    setShowForm(false)
    setEditingContact(null)
    fetchContactDetails()
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingContact(null)
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail size={16} />
      case 'phone': return <Phone size={16} />
      case 'address': return <MapPin size={16} />
      case 'social': return <Globe size={16} />
      case 'note': return <StickyNote size={16} />
      default: return <StickyNote size={16} />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'email': return 'bg-blue-100 text-blue-800'
      case 'phone': return 'bg-green-100 text-green-800'
      case 'address': return 'bg-purple-100 text-purple-800'
      case 'social': return 'bg-orange-100 text-orange-800'
      case 'note': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return <div className="p-6">Loading contact details...</div>
  }

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {editingContact ? 'Edit Contact Detail' : 'Add Contact Detail'}
          </h2>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Contact Detail Information</CardTitle>
            <CardDescription>
              {editingContact ? 'Update the contact detail information' : 'Add a new contact detail'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ContactDetailsForm
              contactDetail={editingContact}
              onSave={handleFormSave}
              onCancel={handleFormCancel}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Contact Details</h2>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Contact Detail
        </Button>
      </div>

      <div className="grid gap-4">
        {contactDetails.map((contact) => (
          <Card key={contact.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getTypeIcon(contact.type)}
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold">{contact.label}</h3>
                      <Badge className={getTypeColor(contact.type)}>
                        {contact.type}
                      </Badge>
                      {!contact.is_active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                    <p className="text-gray-600 mb-3">{contact.value}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <span>Contact Page:</span>
                        <Badge variant={contact.show_in_contact_page ? "default" : "secondary"}>
                          {contact.show_in_contact_page ? "Visible" : "Hidden"}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>Footer:</span>
                        <Badge variant={contact.show_in_footer ? "default" : "secondary"}>
                          {contact.show_in_footer ? "Visible" : "Hidden"}
                        </Badge>
                      </div>
                      <span>Order: {contact.display_order}</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingContact(contact)
                      setShowForm(true)
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(contact.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {contactDetails.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-500 mb-4">No contact details found.</p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Contact Detail
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default ContactDetailsManager